import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LineId } from '@mrt/graph';
import { MapCanvas } from './components/MapCanvas.js';
import { GlassHeader, LineTray, StatusBanner } from './components/Chrome.js';
import { useLiveState } from './lib/useLiveState.js';
import { loadLang, saveLang, t } from './lib/i18n.js';
import type { Lang } from './lib/i18n.js';

export function App() {
  const { graph, occupancies, status, lastSnapshotAt, clockOffsetMs, graphError } = useLiveState();
  const [lang, setLang] = useState<Lang>(loadLang);
  const [hidden, setHidden] = useState<Set<LineId>>(new Set());
  // No animation frames at all while backgrounded.
  const [visible, setVisible] = useState(() => !document.hidden);

  useEffect(() => {
    const onVisibility = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang === 'zh' ? 'zh-Hant' : 'en';
  }, [lang]);

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next = prev === 'zh' ? 'en' : 'zh';
      saveLang(next);
      return next;
    });
  }, []);

  const visibleLines = useMemo(() => {
    const all = new Set<LineId>((graph?.lines ?? []).map((l) => l.id));
    for (const id of hidden) all.delete(id);
    return all;
  }, [graph, hidden]);

  const toggleLine = useCallback((id: LineId) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const showAll = useCallback(() => setHidden(new Set()), []);

  const s = t(lang);
  const shownTrains = occupancies.filter((o) => visibleLines.has(o.lineId)).length;

  return (
    <div className="app">
      <div className="glow g1" aria-hidden="true" />
      <div className="glow g2" aria-hidden="true" />

      <GlassHeader
        lang={lang}
        status={status}
        lastSnapshotAt={lastSnapshotAt}
        trains={shownTrains}
        onToggleLang={toggleLang}
      />

      <main className="map">
        {graph ? (
          <MapCanvas
            graph={graph}
            occupancies={occupancies}
            visibleLines={visibleLines}
            lang={lang}
            clockOffsetMs={clockOffsetMs}
            animate={visible}
            dimmed={status !== 'live'}
          />
        ) : (
          <div className="placeholder">{graphError ? s.down : s.connecting}</div>
        )}
        <StatusBanner lang={lang} status={status} trains={shownTrains} />
      </main>

      {graph && <LineTray graph={graph} lang={lang} visible={visibleLines} onToggle={toggleLine} onAll={showAll} />}
    </div>
  );
}
